import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Crown, Star, Users } from "lucide-react";
import type { EventItem } from "./CommunityStats";

const ICON_MAP = {
  explorers: Users,
  vip: Crown,
  cosmic: Star,
  readings: Sparkles,
};

const COLOR_MAP = {
  explorers: "text-primary",
  vip: "text-cosmic-gold",
  cosmic: "text-purple-400",
  readings: "text-cosmic-teal",
};

const FALLBACK_MESSAGES: EventItem[] = [
  { text: "Emma from London unlocked her birth chart ✨", icon: "readings" },
  { text: "Alex started a compatibility reading 💫", icon: "readings" },
  { text: "Sophia upgraded to VIP 👑", icon: "vip" },
  { text: "James checked his daily horoscope 🔮", icon: "readings" },
  { text: "Luna from Paris asked the AI Astrologer 🌙", icon: "readings" },
  { text: "Oliver discovered his soulmate match 💕", icon: "readings" },
  { text: "Noah found his lucky day 🍀", icon: "readings" },
  { text: "Charlotte upgraded to Cosmic Tier 🏆", icon: "cosmic" },
];

const ActivityFeed = () => {
  const [queue, setQueue] = useState<EventItem[]>([]);
  const [current, setCurrent] = useState<EventItem | null>(null);
  const [visible, setVisible] = useState(false);
  const [fallbackIdx, setFallbackIdx] = useState(0);

  const pushEvents = useCallback((events: EventItem[]) => {
    setQueue((prev) => [...prev, ...events]);
  }, []);

  // Expose pushEvents globally so CommunityStats can call it
  useEffect(() => {
    (window as any).__nebulaActivityPush = pushEvents;
    return () => { delete (window as any).__nebulaActivityPush; };
  }, [pushEvents]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    const showNext = () => {
      const next = queue.length > 0
        ? queue[0]
        : FALLBACK_MESSAGES[fallbackIdx % FALLBACK_MESSAGES.length];

      if (queue.length > 0) {
        setQueue((prev) => prev.slice(1));
      } else {
        setFallbackIdx((prev) => prev + 1);
      }

      setCurrent(next);
      setVisible(true);

      timeout = setTimeout(() => {
        setVisible(false);
        timeout = setTimeout(showNext, Math.random() * 15000 + 5000);
      }, 5000);
    };

    timeout = setTimeout(showNext, 2000);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const Icon = current ? ICON_MAP[current.icon] : Sparkles;
  const color = current ? COLOR_MAP[current.icon] : "text-cosmic-gold";

  return (
    <div className="fixed bottom-4 left-4 z-50 max-w-[320px]">
      <AnimatePresence>
        {visible && current && (
          <motion.div
            key={current.text}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="rounded-xl px-4 py-3 backdrop-blur-xl bg-card/80 border border-primary/20 text-sm text-muted-foreground"
            style={{ boxShadow: "0 0 20px hsl(270 60% 60% / 0.15)" }}
          >
            <div className="flex items-start gap-2">
              <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${color}`} />
              <span>{current.text}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityFeed;
